import axios from 'axios';
import { api, isTransientProxyError } from './api';

export interface PricingParams {
    kind: 'image' | 'video' | 'audio' | 'tool';
    model_id: string;
    params: Record<string, any>;
}

export interface QuoteBreakdown {
    base_cost: number;
    modifiers_applied: Array<{
        param: string;
        match: string;
        type: string;
        value: number;
        before: number;
        after: number;
    }>;
    provider_cost_credits: number;
    margin_multiplier: number;
    rounding_mode: string;
    nice_step: number | null;
    final_user_price_credits: number;
}

export interface QuoteResponse {
    user_price_credits: number;
    provider_cost_credits: number;
    pricing_version: number;
    breakdown: QuoteBreakdown;
    normalized_params: Record<string, any>;
}

export interface PricingVersion {
    id: string;
    version: number;
    status: 'draft' | 'published' | 'archived' | 'unavailable';
    published_at: string | null;
    comment: string | null;
    created_at: string;
}

export async function fetchQuote(payload: PricingParams): Promise<QuoteResponse> {
    const response = await api.post<QuoteResponse>('/pricing/quote', payload);
    return response.data;
}

export async function getPricingVersions(): Promise<PricingVersion[]> {
    const response = await api.get<PricingVersion[]>('/pricing/admin/versions');
    return response.data;
}

export async function getCurrentPricing(): Promise<PricingVersion> {
    try {
        const response = await api.get<PricingVersion>('/pricing/admin/current', { timeout: 10000 });
        return response.data;
    } catch (error) {
        const status = axios.isAxiosError(error) ? error.response?.status : undefined;
        if (!isTransientProxyError(error) && status !== 502 && status !== 503 && status !== 504) {
            throw error;
        }
        return {
            id: "pricing-unavailable",
            version: 0,
            status: "unavailable",
            published_at: null,
            comment: "Pricing service unavailable",
            created_at: new Date().toISOString()
        };
    }
}

export async function clonePricingVersion(sourceVersion: number, comment: string = "Cloned version"): Promise<PricingVersion> {
    const response = await api.post(`/pricing/admin/versions/${sourceVersion}/clone`, null, {
        params: { comment }
    });
    return response.data;
}

export async function publishPricingVersion(versionId: string): Promise<void> {
    await api.post(`/pricing/admin/versions/${versionId}/publish`);
}

export interface PricingVariantItem {
    id: string;
    model_id: string;
    kind: string;
    base_cost_credits: number;
    [key: string]: unknown;
}

export async function getPricingVariants(versionId: string): Promise<PricingVariantItem[]> {
    const response = await api.get(`/pricing/admin/variants/${versionId}`);
    return response.data;
}

export async function updateVariantPrice(variantId: string, baseCostCredits: number): Promise<void> {
    await api.post(`/pricing/admin/variants/${variantId}`, null, {
        params: { base_cost_credits: baseCostCredits }
    });
}

export interface PricingOverride {
    id: string;
    model_id: string;
    kind: string;
    params_json: Record<string, any>;
    override_user_price_credits: number | null;
    override_provider_cost_credits: number | null;
    is_active: boolean;
}

export interface OverridesListResponse {
    total: number;
    page: number;
    limit: number;
    items: PricingOverride[];
}

export async function getPricingOverrides(
    versionId: string, 
    params: { model_id?: string; kind?: string; page?: number; limit?: number; only_overridden?: boolean; _t?: number }
): Promise<OverridesListResponse> {
    try {
        const response = await api.get('/pricing/admin/overrides', {
            params: { pricing_version_id: versionId, ...params },
            timeout: 10000
        });
        return response.data;
    } catch (error) {
        if (!isTransientProxyError(error)) {
            throw error;
        }
        return {
            total: 0,
            page: params.page ?? 1,
            limit: params.limit ?? 50,
            items: []
        };
    }
}

export async function updatePricingOverride(
    overrideId: string, 
    data: { override_user_price_credits?: number | null; is_active?: boolean }
): Promise<void> {
    await api.patch(`/pricing/admin/overrides/${overrideId}`, data);
}

export async function generatePricingOverrides(
    versionId: string,
    modelId: string,
    kind: string,
    mode: 'minimal' | 'full' = 'minimal'
): Promise<{ created: number; updated: number; skipped: number; total: number }> {
    const response = await api.post('/pricing/admin/overrides/generate', {
        pricing_version_id: versionId,
        model_id: modelId,
        kind: kind,
        mode: mode
    });
    return response.data;
}

export async function syncPricingOverrides(versionId: string): Promise<{ created: number; updated: number; skipped: number; total: number }> {
    const response = await api.post('/pricing/admin/overrides/sync', {
        pricing_version_id: versionId
    });
    return response.data;
}
